import { useMemo, useRef, useState, useEffect } from 'react';

/**
 * Zeigt HTML-Mailinhalt sicher an.
 *
 * Sicherheitsmodell (mehrere unabhängige Schutzebenen):
 * 1. Der Server liefert bereits bereinigtes HTML (bleach-Whitelist, siehe
 *    backend/apps/mail_processing/imap_service.py::sanitize_mail_html) —
 *    kein <script>, kein <style>, keine Event-Handler-Attribute, keine
 *    gefährlichen Tags/Protokolle.
 * 2. Zusätzlich wird hier client-seitig in einem sandboxed <iframe>
 *    gerendert OHNE "allow-scripts" — selbst wenn ein Skript die
 *    Server-Sanitisierung überstehen würde, könnte es nicht ausgeführt
 *    werden. "allow-same-origin" ist gesetzt (nur damit die Höhe des
 *    Inhalts gemessen werden kann), "allow-popups" erlaubt target="_blank"
 *    Links zum Öffnen in einem neuen Tab.
 * 3. Externe Bilder werden standardmäßig blockiert (Tracking-Pixel/Privacy),
 *    der Nutzer kann sie bei Bedarf über einen Button laden.
 * 4. Alle Links wurden serverseitig bereits mit target="_blank"
 *    rel="noopener noreferrer" versehen.
 */
export default function SafeHtmlMail({ html, className = '' }) {
  const iframeRef = useRef(null);
  const [imagesAllowed, setImagesAllowed] = useState(false);
  const [height, setHeight] = useState(200);

  const hasBlockedImages = useMemo(() => {
    if (!html) return false;
    return /<img\s[^>]*\ssrc\s*=\s*["']https?:/i.test(html);
  }, [html]);

  const srcDoc = useMemo(() => {
    if (!html) return '';
    let content = html;
    if (!imagesAllowed) {
      // Externe (http/https) Bildquellen blockieren, cid:/data: URIs
      // (eingebettete Bilder, kein Tracking-Risiko) unangetastet lassen.
      content = content.replace(
        /(<img\s[^>]*\ssrc\s*=\s*["'])(https?:)([^"']*["'])/gi,
        '$1about:blank#blocked-external-image$3'
      );
    }
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<base target="_blank">
<style>
  html, body {
    margin: 0;
    padding: 12px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #1f2937;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: #2563eb; }
  * { max-width: 100%; box-sizing: border-box; }
</style>
</head>
<body>${content}</body>
</html>`;
  }, [html, imagesAllowed]);

  useEffect(() => {
    setHeight(200);
  }, [srcDoc]);

  const handleLoad = () => {
    try {
      const doc = iframeRef.current?.contentWindow?.document;
      if (doc?.documentElement) {
        const h = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
        if (h > 0) setHeight(Math.min(h + 16, 2000));
      }
    } catch (e) {
      // Falls Höhenmessung aus irgendeinem Grund fehlschlägt: feste Höhe
      // mit eigenem Scrollbereich im iframe belassen, kein Fehler nötig.
    }
  };

  if (!html) return null;

  return (
    <div className={className}>
      {hasBlockedImages && !imagesAllowed && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span>Externe Bilder wurden aus Datenschutzgründen nicht geladen.</span>
          <button
            type="button"
            onClick={() => setImagesAllowed(true)}
            className="shrink-0 rounded bg-amber-100 px-2 py-1 text-amber-900 hover:bg-amber-200"
          >
            Bilder laden
          </button>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Mailinhalt"
        srcDoc={srcDoc}
        onLoad={handleLoad}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{ width: '100%', height, border: 'none', display: 'block' }}
        className="rounded"
      />
    </div>
  );
}
